import { Show, type JSX } from 'solid-js'
import type { PeriodScope } from 'mormaii-web-presentation/period'

import Css from './period-picker.module.scss'
import { DateField, Menu } from '../ui'

/**
 * O seletor de periodo: o atalho num combobox, e as duas datas ao lado.
 *
 * ## As datas ficam SEMPRE visiveis
 *
 * E nao escondidas atras da opcao "Escolher datas". Elas mostram o intervalo EM
 * VIGOR - o presenter preenche `from`/`to` a partir do atalho escolhido -, entao
 * "Últimos 30 dias" responde ali mesmo que dias aquilo significa, e ajustar uma
 * ponta parte do que ja esta na tela em vez de um campo em branco.
 *
 * Mexer numa delas troca o atalho para "Escolher datas" sozinho; quem decide isso e
 * o `PeriodSelectorPresenter`, e nao esta view.
 *
 * ## O aviso do teto
 *
 * Quem hospeda pode ter um teto (o Relatorio de Sono tem cinco semanas). O numero e
 * dito ANTES de alguem esbarrar nele, e o aviso de corte aparece depois - encurtar
 * calado faria o leitor achar que faltam dados no periodo que ele pediu.
 */
export function PeriodPicker(props: {
    scope: PeriodScope
    /** Sobre a faixa escura de filtros, que e onde ele aparece hoje. */
    onDark?: boolean

    /**
     * O nome do recorte, em cima do combo de atalho.
     *
     * No combo e nao em volta dos tres controles: ali ele fica na MESMA linha de
     * "DE" e "ATE", e com isso na mesma linha dos rotulos dos filtros vizinhos.
     * Envolvendo o grupo, o nome subia a altura de um rotulo e a fileira ficava
     * desalinhada - medido na lista de Colaboradores, onde "REGISTROU EM" ficava 20px
     * acima de "TIPO DE MEDIÇÃO" ao lado.
     *
     * Opcional: onde o seletor esta debaixo de um titulo que ja diz do que se trata,
     * nomea-lo de novo e a mesma informacao duas vezes.
     */
    label?: string
    class?: string
}): JSX.Element {
    return (
        <div class={`${Css.picker} ${props.class ?? ''}`}>
            <Menu
                onDark={props.onDark}
                label={props.label}
                class={Css.preset}
                summary={props.scope.summary}
                /*
                    "Todo o periodo" e ausencia de recorte, e nao uma escolha - ver
                    `PeriodScope.applied`. Sem isto o seletor era o unico controle da
                    fileira que parecia sempre preenchido.
                */
                empty={!props.scope.applied}
                options={props.scope.options.map((option) => ({
                    label: option.label,
                    current: option.current,
                    onSelect: option.onSelect
                }))}
            />

            <DateField
                onDark={props.onDark}
                label="De"
                value={props.scope.from}
                max={props.scope.to}
                onChange={(value) => props.scope.onFromChanged(value)}
                class={Css.date}
            />

            <DateField
                onDark={props.onDark}
                label="Até"
                value={props.scope.to}
                min={props.scope.from}
                onChange={(value) => props.scope.onToChanged(value)}
                class={Css.date}
            />
        </div>
    )
}

/** O que o teto de quem hospeda diz, quando ha teto. */
export function PeriodLimitNote(props: { scope: PeriodScope; onDark?: boolean }): JSX.Element {
    return (
        <Show when={props.scope.maxDays > 0}>
            <p class={Css.note} classList={{ [Css.clamped]: props.scope.clamped, [Css.onDark]: props.onDark }}>
                <Show when={props.scope.clamped} fallback={`O período vai até ${props.scope.maxDays} dias.`}>
                    Período encurtado para o limite de {props.scope.maxDays} dias.
                </Show>
            </p>
        </Show>
    )
}
